import { motion } from "motion/react";
import { DownloadCard } from "@/components/download/download-card.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import type { ReleasePlatform, ReleaseSummary } from "@/lib/api.ts";
import { useReleaseCatalog } from "@/lib/api.ts";
import { detectOperatingSystem } from "@/lib/os-detect.ts";

const ease = [0.22, 1, 0.36, 1] as const;

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease },
  },
};

const platforms: ReleasePlatform[] = ["windows", "macos", "linux"];

function findLatestForPlatform(
  releases: ReleaseSummary[],
  platform: ReleasePlatform,
) {
  for (const release of releases) {
    const asset = release.assets.find((a) => a.platform === platform);
    if (asset) {
      return { release, asset };
    }
  }
  return { release: null, asset: null };
}

export function DownloadPage() {
  const catalog = useReleaseCatalog();
  const detectedPlatform = detectOperatingSystem();

  if (catalog.isLoading) {
    return (
      <section className="mx-auto flex h-full w-full max-w-4xl items-center justify-center px-5 py-12">
        <div className="grid w-full gap-6 sm:grid-cols-3">
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="aspect-square w-full" />
        </div>
      </section>
    );
  }

  if (catalog.isError || !catalog.data) {
    return (
      <section className="mx-auto flex h-full w-full max-w-5xl px-5 py-5">
        <Card className="w-full border-destructive/30">
          <CardHeader>
            <CardTitle>Release stream unavailable</CardTitle>
            <CardDescription>
              Could not fetch the latest release from GitHub. The upstream token or deploy
              configuration may need attention.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-foreground/72 text-sm">
            {catalog.error instanceof Error
              ? catalog.error.message
              : "Unknown error while reading the release catalog."}
          </CardContent>
        </Card>
      </section>
    );
  }

  const releases = catalog.data;

  return (
    <section className="mx-auto flex h-full w-full max-w-4xl items-center justify-center px-5 py-12">
      <motion.div
        className="grid w-full gap-6 sm:grid-cols-3"
        variants={stagger}
        initial="hidden"
        animate="show"
      >
        {platforms.map((platform) => {
          const { release, asset } = findLatestForPlatform(releases, platform);
          return (
            <motion.div key={platform} variants={fadeUp}>
              <DownloadCard
                platform={platform}
                asset={asset}
                version={release?.tagName ?? null}
                detected={platform === detectedPlatform}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}
